import { z } from "zod";

/**
 * `QUOTITE-OPERANDS` (architect's ruling, 22 October 2026, option B): **the quotité point's answer carries the operands it was computed from**, typed once for
 * the bank's door and the console's. They are the operands of the grade's CAPACITÉ (the instalment is taken at the grid's highest row, at the term asked, else the
 * maximum); `capacityUsedPct` is the grade's own figure, « part de la quotité cessible utilisée » (never « quotité »). **P15's quotité is NOT among them**: it stays
 * the stored submission figure (`QUOTITE-ONE` books the two). Amounts are whole FCFA. **Not computed is every operand `null` with its cause , never 0.**
 *
 * The OpenAPI document stays an inventory until `P0-INF-026` (no body types); this schema is the shared type, parsed from the real doors by their integration spec.
 */

/** Where the instalment's rate came from: the grid's HIGHEST row present (whatever the dossier's letter), or the mandate's rate at the console (no letter). */
export const rateSourceSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("highestRow"), grade: z.string() }),
  z.strictObject({ kind: z.literal("mandate") }),
]);

/** Why the operands are absent, in the bank's words. */
export const notComputedSchema = z.strictObject({ cause: z.string(), words: z.string() });

export const capacityOperandsSchema = z.strictObject({
  /** The term the Capacité is computed at: the term asked, else the policy's maximum. `null` at the console (the mandate's instalment carries none). */
  termMonths: z.number().int().nullable(),
  termBasis: z.enum(["asked", "maximum"]).nullable(),
  retainedNetAmount: z.number().int().nullable(),
  retainedCommitmentsAmount: z.number().int().nullable(),
  assignableSharePct: z.number().nullable(),
  assignableShareAmount: z.number().int().nullable(),
  instalmentAmount: z.number().int().nullable(),
  /** A one-decimal string (« 11.2 »), as the rates travel. */
  ratePct: z.string().nullable(),
  rateSource: rateSourceSchema.nullable(),
  /** The grade's own function: (commitments + instalment) ÷ the cessible amount, one decimal. */
  capacityUsedPct: z.number().nullable(),
  /**
   * `REMAINING-CAPACITY`: the instalment ÷ (the assignable share − the retained commitments), one decimal, at the same term as the other operands.
   * Printed only as « part de la capacité restante »; it decides nothing (never the grade, P15 or a block). `null` when the Capacité is not computed
   * (the cause is `notComputed`) or when the share is used up by the commitments (the cause is `remainingCapacityNotComputed`, « capacité disponible nulle »).
   */
  remainingCapacityUsedPct: z.number().nullable(),
  remainingCapacityNotComputed: notComputedSchema.nullable(),
  /**
   * `CAPACITY-VERDICT`: what the part used means, said by the server so no screen compares with 100. `exceeds` where the retained commitments plus the instalment are
   * more than the cessible amount, `within` where they are not, decided on the exact integers and never on the rounded `capacityUsedPct`; `null` exactly when the
   * operands are not computed. `capacityWords` is its sentence, display only.
   */
  capacityVerdict: z.enum(["within", "exceeds"]).nullable(),
  capacityWords: z.string().nullable(),
  /**
   * `ARC2-DATA` (the sheet bar's total): the retained commitments plus the instalment, one exact integer, so that no screen adds amounts (D-1020-7). `null` exactly
   * when the Capacité is not computed or either term is absent; derived from the run's own integers for a run stored before it.
   */
  commitmentsAndInstalmentAmount: z.number().int().nullable(),
  notComputed: notComputedSchema.nullable(),
});

const boxSchema = z.strictObject({
  page: z.number(),
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
});

const figuresEvidenceSchema = z.strictObject({
  kind: z.literal("figures"),
  compared: z.record(z.string(), z.union([z.number(), z.string(), z.null()])),
  operands: capacityOperandsSchema.optional(),
  /** `QUOTITE-EXACT`: on the quotité point, which figure the verdict was decided on , the integers (`exact`) or the stored whole percent (`arrondi`). */
  ratioBasis: z.enum(["exact", "arrondi"]).optional(),
});

/** The console's reading of where a proposal was read: a pièce (with its document's id) and its page, or figures compared (the quotité point's carry their operands). */
export const consolePointEvidenceSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("document"),
    documentId: z.string().nullable(),
    documentCode: z.string(),
    documentLabel: z.string(),
    page: z.number(),
    box: boxSchema.nullable(),
  }),
  figuresEvidenceSchema,
]);

/** `BANK-IDS`: the bank's reading of the same, its OWN strict schema: the pièce by code and label, never by the document's id (an extra `documentId` key is refused). */
export const bankPointEvidenceSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("document"),
    documentCode: z.string(),
    documentLabel: z.string(),
    page: z.number(),
    box: boxSchema.nullable(),
    /** `ARC1-DATA`: the pièce's stored receipt time, an ISO instant; `null` when the pièce is not received (D2's absent form). */
    receivedAt: z.iso.datetime().nullable(),
  }),
  figuresEvidenceSchema,
]);

export type RateSource = z.infer<typeof rateSourceSchema>;
export type CapacityOperands = z.infer<typeof capacityOperandsSchema>;
export type ConsolePointEvidence = z.infer<typeof consolePointEvidenceSchema>;
export type BankPointEvidence = z.infer<typeof bankPointEvidenceSchema>;

/**
 * `BRIEFING-FIELDS` (architect rulings of 23 October): the four facts of the « nouveau » briefing, read from the stored run, never
 * recomputed and never counted by the browser. A fact the run did not read is `null`, never 0, with its `cause`: `non_lu` (the run
 * looked and the reading was not done) or `inconnue` (a run stored before the fact was kept). The words are lane A's, per code.
 */
export const briefingCauseSchema = z.enum(["non_lu", "inconnue"]);

/** The closed list of the sub-reasons the `apportOrigine` point may carry (workflow v3 section 3.6). */
export const apportSubReasonSchema = z.enum([
  "apport_non_visible",
  "origine_non_coherente",
  "apport_emprunte",
  "origine_a_cribler",
]);

export const briefingFactsSchema = z.strictObject({
  /** The incident count the grade stored, and the number of statements the counter read to count it. */
  incidents: z.strictObject({
    count: z.number().int().nullable(),
    statementsRead: z.number().int().nullable(),
    cause: briefingCauseSchema.nullable(),
  }),
  /** The evidenced apport as the reader stored it (whole francs, `null` when none) and its sub-reason (`null`: nothing flagged). */
  apport: z.strictObject({
    evidencedAmount: z.number().int().nullable(),
    subReason: apportSubReasonSchema.nullable(),
    cause: briefingCauseSchema.nullable(),
  }),
  /** The pièces the readers read: the count alone, no completeness fact is stored. */
  documentsRead: z.strictObject({
    count: z.number().int().nullable(),
    cause: briefingCauseSchema.nullable(),
  }),
  /** `toCheck`: the points whose stored verdict is `non_conforme` or `incertain` and that have no effective analyst act. */
  pointsToCheck: z.strictObject({ read: z.number().int(), toCheck: z.number().int() }),
});
export type BriefingFacts = z.infer<typeof briefingFactsSchema>;
